export type ApiErrorKind = "network" | "timeout" | "aborted" | "http" | "parse";

/** Safe scalar extras the API renders inside `error` (e.g. `attempts_remaining`, `captcha_required`). */
export type ErrorDetails = Readonly<Record<string, string | number | boolean | null>>;

export interface ApiErrorInit {
  kind: ApiErrorKind;
  status: number;
  code: string;
  message: string;
  requestId?: string | undefined;
  fields?: Readonly<Record<string, string>> | undefined;
  details?: ErrorDetails | undefined;
  retryAfter?: number | undefined;
}

/** Fallback copy when the server sent no user-presentable message (transport failures, HTML 502s…). */
const SAFE_MESSAGES: Record<ApiErrorKind, string> = {
  network: "We couldn't reach the server. Check your connection and try again.",
  timeout: "The server took too long to respond. Please try again.",
  aborted: "The request was cancelled.",
  http: "Something went wrong. Please try again.",
  parse: "We received an unexpected response. Please try again.",
};

const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  400: "Some details look wrong. Check the form and try again.",
  401: "Your session has ended. Please sign in again.",
  402: "This feature isn't included in your current plan.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: "This was changed by someone else. Refresh and try again.",
  413: "That file or request is too large.",
  422: "Some details look wrong. Check the form and try again.",
  429: "Too many requests. Please wait a moment and try again.",
};

const MAX_MESSAGE_LENGTH = 300;

/**
 * Normalized error for every failure mode: `{code, message, fields, requestId}`.
 * `message` is the API's own user-presentable text (backend contract: never stack traces or
 * provider errors) or a safe local fallback — never a raw framework `detail`.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly fields: Readonly<Record<string, string>>;
  readonly details: ErrorDetails;
  readonly retryAfter: number | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.fields = init.fields ?? {};
    this.details = init.details ?? {};
    this.retryAfter = init.retryAfter;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isPlanGate(): boolean {
    return this.status === 402;
  }

  get isRetryable(): boolean {
    return this.kind === "network" || this.kind === "timeout" || this.status === 429 || this.status >= 500;
  }

  /** Numeric detail (e.g. `attempts_remaining`), or undefined. */
  detailNumber(key: string): number | undefined {
    const value = this.details[key];
    return typeof value === "number" ? value : undefined;
  }

  detailFlag(key: string): boolean {
    return this.details[key] === true;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function transportError(kind: Exclude<ApiErrorKind, "http">, status = 0): ApiError {
  return new ApiError({ kind, status, code: kind, message: SAFE_MESSAGES[kind] });
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const ENVELOPE_KEYS = new Set(["code", "message", "request_id", "fields"]);

function isScalar(value: unknown): value is string | number | boolean | null {
  return value === null || ["string", "number", "boolean"].includes(typeof value);
}

/** `fields: [{field, message}]` (our envelope) or FastAPI `detail: [{loc, msg}]` → `{ field: message }`. */
function fieldErrorsFrom(envelope: Record<string, unknown>, body: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  const fields = envelope.fields;
  if (Array.isArray(fields)) {
    for (const item of fields) {
      if (isRecord(item) && typeof item.field === "string" && typeof item.message === "string" && item.field) out[item.field] = item.message;
    }
  }
  const detail = body.detail;
  if (Array.isArray(detail)) {
    for (const item of detail) {
      if (!isRecord(item) || !Array.isArray(item.loc) || typeof item.msg !== "string") continue;
      const field = item.loc.filter((part) => part !== "body" && part !== "query" && part !== "path").join(".");
      if (field) out[field] = item.msg;
    }
  }
  return out;
}

function detailsFrom(envelope: Record<string, unknown>): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(envelope)) {
    if (!ENVELOPE_KEYS.has(key) && isScalar(value)) out[key] = value;
  }
  return out;
}

function codeFrom(envelope: Record<string, unknown>, body: Record<string, unknown>, status: number): string {
  if (typeof envelope.code === "string") return envelope.code;
  if (typeof body.code === "string") return body.code;
  if (typeof body.error === "string") return body.error;
  return `http_${String(status)}`;
}

function messageFrom(envelope: Record<string, unknown>, status: number): string {
  const message = envelope.message;
  if (typeof message === "string" && message.trim() && message.length <= MAX_MESSAGE_LENGTH) return message;
  return STATUS_MESSAGES[status] ?? SAFE_MESSAGES.http;
}

export interface HttpErrorMeta {
  requestId?: string | undefined;
  retryAfter?: string | null | undefined;
}

/** Builds a normalized ApiError from a non-2xx response body (`{"error": {...}}` envelope or legacy shapes). */
export function httpError(status: number, body: unknown, meta: HttpErrorMeta = {}): ApiError {
  const root = isRecord(body) ? body : {};
  const envelope = isRecord(root.error) ? root.error : {};
  const retryAfter = meta.retryAfter ? Number.parseInt(meta.retryAfter, 10) : Number.NaN;
  return new ApiError({
    kind: "http",
    status,
    code: codeFrom(envelope, root, status),
    message: messageFrom(envelope, status),
    requestId: typeof envelope.request_id === "string" ? envelope.request_id : meta.requestId,
    fields: fieldErrorsFrom(envelope, root),
    details: detailsFrom(envelope),
    retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
  });
}
